import { DEFAULT_TIMEZONE } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { useMemo } from "react";
import { useWorkspaceDetails } from "../../../session/workspace-context";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export interface Formatters {
  locale: string;
  timeZone: string;
  time: (iso: string | null | undefined) => string;
  date: (iso: string | null | undefined) => string;
  dateTime: (iso: string | null | undefined) => string;
  /** "now", "5m", "3h", "Yesterday", then a short date (list timestamps). */
  ago: (iso: string | null | undefined, now?: number) => string;
  number: (value: number) => string;
  percent: (ratio: number | null | undefined, digits?: number) => string;
  peso: (value: number) => string;
}

/** Workspace-TZ formatters (CLAUDE.md rule 15: dates in workspace TZ, money via Intl currency formats). */
export function useFormatters(): Formatters {
  const { t, i18n } = useTranslation();
  const workspace = useWorkspaceDetails();
  const timeZone = workspace.data?.timezone ?? DEFAULT_TIMEZONE;
  const locale = i18n.language === "fil" ? "fil-PH" : "en-PH";
  return useMemo(() => {
    const timeFmt = new Intl.DateTimeFormat(locale, { timeZone, hour: "numeric", minute: "2-digit" });
    const dateFmt = new Intl.DateTimeFormat(locale, { timeZone, month: "short", day: "numeric" });
    const dateTimeFmt = new Intl.DateTimeFormat(locale, { timeZone, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    const numberFmt = new Intl.NumberFormat(locale);
    const pesoFmt = new Intl.NumberFormat(locale, { style: "currency", currency: "PHP", maximumFractionDigits: 0 });
    const safe = (fmt: Intl.DateTimeFormat) => (iso: string | null | undefined) => (iso ? fmt.format(new Date(iso)) : "");
    return {
      locale,
      timeZone,
      time: safe(timeFmt),
      date: safe(dateFmt),
      dateTime: safe(dateTimeFmt),
      ago: (iso, now = Date.now()) => {
        if (!iso) return "";
        const diff = now - new Date(iso).getTime();
        if (diff < 0) return dateFmt.format(new Date(iso));
        if (diff < MINUTE_MS) return t("engage.time.now");
        if (diff < HOUR_MS) return t("engage.time.minutes", { count: Math.floor(diff / MINUTE_MS) });
        if (diff < DAY_MS) return t("engage.time.hours", { count: Math.floor(diff / HOUR_MS) });
        if (diff < 2 * DAY_MS) return t("engage.time.yesterday");
        return dateFmt.format(new Date(iso));
      },
      number: (value) => numberFmt.format(value),
      percent: (ratio, digits = 0) => (ratio === null || ratio === undefined ? "—" : `${(ratio * 100).toFixed(digits)}%`),
      peso: (value) => pesoFmt.format(value),
    };
  }, [locale, timeZone, t]);
}
